import type { Discipline, IssueStatus, UserProfile } from "@/domains/pm/types";
import type { PMTranslations } from "@/lib/i18n/types/pm";
import { getDisciplineCode } from "@/domains/pm/utils/disciplineName";

/**
 * WHA-3423 — the active filter selection for the PM issues list. The
 * assignee/discipline arrays empty + the two booleans undefined means
 * "no filter applied". Kept structurally identical to the subset of
 * `PMIssueFilters` it drives so wiring is a straight spread.
 */
export interface IssueFilterValue {
  assigneeUserIds: number[];
  disciplineIds: number[];
  hasLinkedDocuments?: boolean;
  hasComments?: boolean;
  /** Empty = unfiltered, mirroring the other multi-selects. Applied
   *  SERVER-side (PMIssueFilters.status) — see effectiveIssueFilters. */
  status: IssueStatus[];
  /** In-progress issues past their due date. A conjunction of status and
   *  date, not a status value, which is why it is its own dimension. */
  overdueOnly?: boolean;
}

export const EMPTY_ISSUE_FILTER: IssueFilterValue = {
  assigneeUserIds: [],
  disciplineIds: [],
  status: [],
};

export type IssueFilterDimension =
  | "status"
  | "assignee"
  | "discipline"
  | "documents"
  | "comments"
  | "overdue";

function userName(u: UserProfile): string {
  const n = `${u.first_name ?? ""} ${u.last_name ?? ""}`.trim();
  return n || u.email;
}

/** One active-filter chip, rendered by `IssueFilterChips` next to the control. */
export interface IssueFilterChip {
  key: IssueFilterDimension;
  /** The whole chip in words — "Assignee: Mari Tamm +2". Used for aria labels. */
  label: string;
  /** The part that may be truncated when the chip hits its width cap. */
  text: string;
  /** "+2" — how many more values are selected. Rendered outside the truncation. */
  more?: string;
  onRemove: () => void;
}

/**
 * A multi-select dimension as a chip: the first value, plus a "+N" for the
 * rest. The two are kept apart because the chip caps its width and a value can
 * be long (a user with no name falls back to their e-mail) — `text` truncates,
 * `more` never does, so the "+N" cannot be the bit that gets cut off.
 * Falls back to the raw count while the values are still loading.
 */
function multiValueChip(
  key: IssueFilterDimension,
  prefix: string,
  values: string[],
  selectedCount: number,
  onRemove: () => void
): IssueFilterChip {
  const text = `${prefix}: ${values.length ? values[0] : selectedCount}`;
  const more = values.length > 1 ? `+${values.length - 1}` : undefined;
  return { key, text, more, label: more ? `${text} ${more}` : text, onRemove };
}

/** A yes/no dimension as a chip — one value, so never a "+N". */
function booleanChip(
  key: IssueFilterDimension,
  label: string,
  onRemove: () => void
): IssueFilterChip {
  return { key, label, text: label, onRemove };
}

type FilterTranslations = PMTranslations["issues"]["filterBy"];

/**
 * The active filters as chips — one per dimension that has a selection.
 * Pure, and deliberately outside the control: the toolbar renders the chips
 * on the OTHER side of the button cluster (they grow leftward into the empty
 * header space), so adding or removing one never shifts the buttons.
 */
export function buildIssueFilterChips(
  value: IssueFilterValue,
  users: UserProfile[],
  disciplines: Discipline[],
  f: FilterTranslations,
  /** UI language — picks the Estonian or English discipline codes. */
  language: string,
  onChange: (next: IssueFilterValue) => void,
  /** Dimensions the host hides (My Issues owns overdue via its own tabs).
   *  A hidden dimension must not produce a chip — there would be no control
   *  behind it to reopen. */
  hiddenDimensions: IssueFilterDimension[] = [],
  statusLabels?: Record<IssueStatus, string>
): IssueFilterChip[] {
  const chips: IssueFilterChip[] = [];
  const shown = (d: IssueFilterDimension) => !hiddenDimensions.includes(d);

  // Overdue pins status to in_progress in the query, so while it is on the
  // user's status selection is overridden rather than applied. It is kept in
  // `value` (switching overdue off restores it) but must not be chipped —
  // a chip is a claim the query honours it.
  if (shown("status") && !value.overdueOnly && value.status.length > 0) {
    const labels = value.status.map((s) => statusLabels?.[s] ?? s);
    chips.push(
      multiValueChip("status", f.status, labels, value.status.length, () =>
        onChange({ ...value, status: [] })
      )
    );
  }
  if (shown("assignee") && value.assigneeUserIds.length > 0) {
    const names = value.assigneeUserIds
      .map((id) => users.find((u) => u.id === id))
      .filter((u): u is UserProfile => !!u)
      .map(userName);
    chips.push(
      multiValueChip("assignee", f.assignee, names, value.assigneeUserIds.length, () =>
        onChange({ ...value, assigneeUserIds: [] })
      )
    );
  }
  if (shown("discipline") && value.disciplineIds.length > 0) {
    const codes = value.disciplineIds
      .map((id) => disciplines.find((d) => d.id === id))
      .filter((d): d is Discipline => !!d)
      .map((d) => getDisciplineCode(d, language))
      // Disciplines sharing a code (KT and VN both read KVJ) are one value on
      // the chip, not "KVJ +1" naming the same code twice.
      .filter((code, i, all) => all.indexOf(code) === i);
    chips.push(
      multiValueChip("discipline", f.discipline, codes, value.disciplineIds.length, () =>
        onChange({ ...value, disciplineIds: [] })
      )
    );
  }
  if (shown("documents") && value.hasLinkedDocuments !== undefined) {
    chips.push(
      // The control only sets `true` or clears now — a switch has nowhere to
      // put a third state. `false` stays labelled correctly because the
      // repository still supports it; a chip reading "Has linked documents"
      // over a has-none filter would be worse than an unused branch.
      booleanChip("documents", value.hasLinkedDocuments ? f.hasDocuments : f.noDocuments, () =>
        onChange({ ...value, hasLinkedDocuments: undefined })
      )
    );
  }
  if (shown("comments") && value.hasComments !== undefined) {
    chips.push(
      booleanChip("comments", value.hasComments ? f.hasComments : f.noComments, () =>
        onChange({ ...value, hasComments: undefined })
      )
    );
  }

  if (shown("overdue") && value.overdueOnly) {
    chips.push(
      booleanChip("overdue", f.overdue, () => onChange({ ...value, overdueOnly: undefined }))
    );
  }

  return chips;
}
